/**
 * TULIA FORK — Premium cart drawer JS
 * Forked from: assets/cart-drawer.js (Dawn 15.3.0)
 *
 * Changes from Dawn original:
 * - getSectionsToRender() references 'tulia-cart-drawer' section
 * - Added cross-sell fetching via Shopify Product Recommendations API
 * - Cross-sell "Add" buttons POST to /cart/add.js and refresh drawer
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.fetchCrossSell();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /* TULIA CHANGE: reference tulia-cart-drawer section */
  getSectionsToRender() {
    return [
      {
        id: 'tulia-cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ============================
   * TULIA ADDITION: Cross-sell
   * ============================ */

  fetchCrossSell() {
    const container = document.getElementById('tulia-cross-sell');
    if (!container) return;

    const productId = container.dataset.productId;
    if (!productId) return;

    const grid = container.querySelector('.tulia-cross-sell__grid');
    if (!grid) return;

    // Get current cart item variant IDs to exclude from recommendations
    const cartItemIds = new Set();
    document.querySelectorAll('[data-quantity-variant-id]').forEach((input) => {
      cartItemIds.add(input.dataset.quantityVariantId);
    });

    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=6&intent=related`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const products = (data.products || []).filter((product) => {
          // Exclude products already in cart
          return !product.variants.some((v) => cartItemIds.has(String(v.id)));
        }).slice(0, 3);

        if (products.length === 0) {
          container.style.display = 'none';
          return;
        }

        container.style.display = '';
        grid.innerHTML = products.map((product) => this.renderCrossSellCard(product)).join('');

        // Attach add-to-cart handlers
        grid.querySelectorAll('.tulia-cross-sell__add').forEach((btn) => {
          btn.addEventListener('click', (e) => this.addCrossSellItem(e));
        });
      })
      .catch(() => {
        // On error, hide the cross-sell section
        container.style.display = 'none';
      });
  }

  renderCrossSellCard(product) {
    const variant = product.variants[0];
    const image = product.featured_image || product.images[0];
    const imageUrl = image ? this.getSizedImageUrl(image, '120x') : '';
    const price = this.formatMoney(variant.price);

    return `
      <div class="tulia-cross-sell__card">
        <div class="tulia-cross-sell__card-image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="44" height="52">` : ''}
        </div>
        <div class="tulia-cross-sell__card-info">
          <div class="tulia-cross-sell__name">${this.escapeHtml(product.title)}</div>
          <div class="tulia-cross-sell__price-row">
            <span class="tulia-cross-sell__price">${price}</span>
            <button
              class="tulia-cross-sell__add"
              type="button"
              data-variant-id="${variant.id}"
              aria-label="Add ${this.escapeHtml(product.title)} to cart"
            >+</button>
          </div>
        </div>
      </div>
    `;
  }

  addCrossSellItem(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    button.classList.add('tulia-cross-sell__add--loading');
    button.disabled = true;

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: this.getSectionsToRender().map((s) => s.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        this.renderContents(parsedState);
      })
      .catch((error) => {
        console.error('Cross-sell add failed:', error);
        button.classList.remove('tulia-cross-sell__add--loading');
        button.disabled = false;
      });
  }

  getSizedImageUrl(src, size) {
    if (typeof src === 'object') src = src.url || src.src || '';
    if (!src) return '';
    // Shopify image URL resize pattern
    return src.replace(/(\.[^.]+)$/, `_${size}$1`).replace('http:', 'https:');
  }

  formatMoney(cents) {
    // Simple USD formatter — matches Shopify's default
    return '$' + (cents / 100).toFixed(2);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /* TULIA CHANGE: reference tulia-cart-drawer section */
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'tulia-cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  /* TULIA CHANGE: override onCartUpdate to fetch from tulia-cart-drawer
   * instead of Dawn's hardcoded cart-drawer section_id in cart.js */
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=tulia-cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
